import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  bulkBlockUnblockContractor,
  getContractors,
  SearchOn,
} from "../../../actions/contractor";
import { showNotifier } from "../../../actions/ui";
import FullScreenLoader from "../../../components/fullscreen-loader";
import { Select } from "../../../components/input-functions";
import { Button } from "../../../components/library/button";
import { Drawer } from "../../../components/library/drawer";
import Filter, { FilterState } from "../../../components/library/filter";
import {
  FunnelIcon,
  PencilSimpleLineIcon,
  PlusIcon,
} from "../../../components/library/icon";
import { IconButton } from "../../../components/library/icon-button";
import Pagination from "../../../components/library/pagination";
import { Popover } from "../../../components/library/popover";
import { SearchInput } from "../../../components/library/search-input";
import { Table, TableColumn } from "../../../components/library/table";
import { useTableState } from "../../../components/library/table/use-table-state";
import Tag from "../../../components/library/tag";
import Modal from "../../../components/modal";
import dispatch from "../../../middleware";
import {
  ContractorActions,
  ContractorDetails,
  NotifierBgColor,
  RoutePaths,
} from "../../../models";
import { ReduxState } from "../../../reducers";
import {
  downloadExcelFromArrayOfObjects,
  formatDate,
  getErrorMsgFromResponse,
  navigateTo,
} from "../../../utils";
import getEffect from "../effect";
import AddContractorForm from "./addContractorForm";

interface ContractorNameLinkProps {
  dataValue: string | boolean;
  rowObj: ContractorDetails;
}

function ContractorNameLink({ dataValue, rowObj }: ContractorNameLinkProps) {
  const path = RoutePaths.CONTRACTOR_PROFILE;
  const query = `?contractorId=${rowObj._id}`;
  return (
    <span
      onClick={() => navigateTo(`${path}${query}`)}
      className="primarycolor cursor-pointer"
    >
      {rowObj.fullName}
    </span>
  );
}

function renderPaginationText(
  total: number,
  page: number,
  perPageCount: number
) {
  const start = perPageCount * (page - 1) + 1;
  const end = start + perPageCount - 1;
  const endCount = end > total ? total : end;
  return `${start} - ${endCount} of ${total}`;
}

interface DownloadButtonProps {
  isPending: boolean;
  selectedRows: ContractorDetails[];
  onClick: (selectedRows: ContractorDetails[], downloadAll: boolean) => void;
}

function DownloadButton({
  isPending,
  selectedRows,
  onClick,
}: DownloadButtonProps) {
  const count = selectedRows.length;
  return (
    <Button
      size="sm"
      onClick={() => onClick(selectedRows, count < 1)}
      loading={isPending}
      title={isPending ? "Processing your download request" : undefined}
    >
      Download {count > 0 ? `(${count})` : ""}
    </Button>
  );
}

function checkIfBlocked(tableFilters: FilterState[]) {
  if (tableFilters.length === 0) return false;
  const currentFilter = tableFilters[0];
  if (currentFilter.operator === "==")
    return currentFilter.leftOperand === currentFilter.rightOperand;
  return false;
}

function handleDownloadExcel(
  data: ContractorDetails[],
  toggleableColumns: {
    key: keyof ContractorDetails;
    label: React.ReactNode;
    visible: boolean;
    disabled: boolean;
  }[]
) {
  const contractorArr = data.map((contractor) => {
    const obj: Record<string, string> = {};
    toggleableColumns.forEach((col) => {
      if (col.label && col.key && col.visible) {
        const filedName = col.label.toString();
        obj[filedName] = String(contractor[col.key]);
      }
    });
    return obj;
  });
  downloadExcelFromArrayOfObjects(contractorArr, "contractors.xlsx");
}

const FILTER_OPTIONS = [
  { label: "All", value: "ALL" },
  { label: "Mobile", value: SearchOn.MOBILE },
  { label: "Contractor ID", value: SearchOn.EMPLOYERS_CONTRACTOR_ID },
];

const ITEMS_PER_PAGE = 10;
export default function Contractors() {
  const storeDispatch = useDispatch();
  const loading = useSelector((state: ReduxState) => state.contractor.loading);
  const contractors: ContractorDetails[] = useSelector(
    (state: ReduxState) => state.contractor.contractors
  );
  const totalNumberOfRecords = useSelector(
    (state: ReduxState) => state.contractor.totalRecords
  );

  const [isAddContractorModalOpen, setIsAddContractorModalOpen] =
    useState(false);
  const [userInputToBeEdited, setIsUserInputToBeEdited] =
    useState<ContractorDetails | null>(null);
  const [isBulkBlockContractorModalOpen, setIsBulkBlockContractorModalOpen] =
    useState(false);
  const [downloadInProgress, _] = useState<boolean>(false);
  const [filterPopoverActive, setFilterPopoverActive] = useState(false);
  const [filteredStatus, setFilteredStatus] = useState<string>("ALL");

  const DATA_TABLE_COLUMNS: TableColumn<ContractorDetails>[] = useMemo(
    () => [
      {
        label: "Full Name",
        key: "fullName",
        render: (_, rowObj) => (
          <ContractorNameLink dataValue="fullName" rowObj={rowObj} />
        ),
        rowActionButtonsVisibleOn: "hover",
        rowActionButtons: (row) => (
          <IconButton
            title="Edit"
            spacing="compact"
            color="brand"
            variant="light"
            onClick={() => {
              setIsUserInputToBeEdited(row);
              setIsAddContractorModalOpen(true);
            }}
          >
            <PencilSimpleLineIcon size="sm" ariaHidden />
          </IconButton>
        ),
        width: "180px",
      },
      {
        label: "Contractor ID",
        key: "employersContractorId",
      },
      {
        label: "Employer ID",
        key: "employerId",
        width: "150px",
      },
      {
        label: "Email ID",
        key: "email",
        width: "180px",
        render: (email) => <span>{email ?? "-"}</span>,
        align: "center",
      },
      {
        label: "Mobile No.",
        key: "mobile",
        width: "100px",
      },
      {
        label: "Transaction Allowed",
        key: "isTransactionAllowed",
        align: "center",
        render: (isTransactionAllowed) => (
          <Tag
            indicatorPosition="left"
            color={isTransactionAllowed ? "success" : "alert"}
          >
            {isTransactionAllowed ? "Yes" : "No"}
          </Tag>
        ),
      },
      {
        label: "Blocked",
        key: "blockedAt",
        render: (blockedAt: string | boolean | undefined) => {
          const isBlocked = !!blockedAt;
          return (
            <Tag
              indicatorPosition="left"
              color={isBlocked ? "alert" : "success"}
            >
              {isBlocked ? "Yes" : "No"}
            </Tag>
          );
        },
        align: "center",
      },
      {
        label: "Created At",
        key: "createdAt",
        width: "150px",
        render: (createdAt: string | boolean, data: ContractorDetails) => {
          const date = formatDate(new Date(createdAt as string));
          return date;
        },
      },
    ],
    []
  );

  const {
    currentPageNumber,
    getTableProps,
    selectedRows,
    setCurrentPageNumber,
    debouncedSearchText,
    setSearchText,
    tableFilters,
    setTableFilters,
    toggleableColumns,
    searchText,
  } = useTableState({
    initialState: {
      tableColumnVisibility: {
        fullName: {
          visible: true,
          disabled: true,
        },
        email: {
          visible: true,
          disabled: true,
        },
        mobile: {
          visible: true,
          disabled: true,
        },
        isTransactionAllowed: {
          visible: true,
          disabled: true,
        },
        createdAt: {
          visible: true,
          disabled: true,
        },
        employersContractorId: {
          visible: true,
          disabled: true,
        },
        employerId: {
          visible: true,
          disabled: true,
        },
        blockedAt: {
          visible: true,
          disabled: true,
        },
      },
    },
    data: contractors,
    columns: DATA_TABLE_COLUMNS,
    config: {
      hasRowSelection: true,
      hasSort: true,
      uniqueKey: "_id",
    },
  });

  useEffect(() => {
    getEffect("Contractors", storeDispatch);
  }, [storeDispatch]);

  useEffect(() => {
    const payload: {
      pageNo: number;
      limit: number;
      searchValue: string;
      searchOn?: SearchOn;
      isBlocked?: boolean;
    } = {
      pageNo: currentPageNumber,
      limit: ITEMS_PER_PAGE,
      searchValue: debouncedSearchText,
      // if the selected status is "ALL", there's no need to apply a search filter 
      searchOn:
        filteredStatus === "ALL" ? undefined : (filteredStatus as SearchOn),
    };

    if (tableFilters.length > 0) {
      const isBlocked = checkIfBlocked(tableFilters);
      payload.isBlocked = isBlocked;
    }

    dispatch(storeDispatch, getContractors(payload));
  }, [
    storeDispatch,
    currentPageNumber,
    tableFilters,
    filteredStatus,
    debouncedSearchText,
  ]);

  async function bulkBlockOrUnblockContractor(action: ContractorActions) {
    if (!selectedRows) return;
    setIsBulkBlockContractorModalOpen(false);
    const _selectedContractorsIds = selectedRows.map((row) => row._id);
    try {
      await dispatch(
        storeDispatch,
        bulkBlockUnblockContractor(_selectedContractorsIds, action)
      );
      const successMessage = `${action}ED ContractorID's: ${_selectedContractorsIds.join(
        ", "
      )}`;

      dispatch(
        storeDispatch,
        showNotifier(NotifierBgColor.SUCCESS, successMessage)
      );
    } catch (error) {
      dispatch(
        storeDispatch,
        showNotifier(NotifierBgColor.ERROR, getErrorMsgFromResponse(error))
      );
    }
  }

  async function handleDownloadClick(data: ContractorDetails[]) {
    if (data.length === 0) {
      dispatch(
        storeDispatch,
        showNotifier(NotifierBgColor.ERROR, "No data to download")
      );
      return;
    }

    return handleDownloadExcel(data, toggleableColumns);
  }

  return (
    <div>
      <FullScreenLoader active={loading} />
      <div className="d-flex justify-content-between flex-md-row flex-column mb-4">
        <Button
          variant="primary"
          color="brand"
          size="md"
          leftSection={<PlusIcon size="md" />}
          style={{ minWidth: "216px" }}
          onClick={() => {
            setIsUserInputToBeEdited(null);
            setIsAddContractorModalOpen(true);
          }}
        >
          Add Contractor
        </Button>
        <div className="d-flex mt-3 mt-md-0" style={{ gap: "16px" }}>
          <Button
            variant="secondary"
            color="brand"
            size="md"
            disabled={selectedRows.length === 0}
            onClick={() =>
              bulkBlockOrUnblockContractor(ContractorActions.UNBLOCK)
            }
          >
            Unblock Contractors
          </Button>
          <Button
            variant="secondary"
            color="danger"
            size="md"
            disabled={selectedRows.length === 0}
            onClick={() => {
              setIsBulkBlockContractorModalOpen(true);
            }}
          >
            Block Contractors
          </Button>
        </div>
      </div>
      <Table
        header={
          <div className="d-flex justify-content-between flex-lg-row flex-column">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "24px",
                width: "100%",
              }}
            >
              <SearchInput
                placeholder="Search"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
              <Select
                value={filteredStatus}
                options={FILTER_OPTIONS}
                fieldName={"Search On"}
                onChange={(f: string, val: string | number) =>
                  setFilteredStatus(String(val))
                }
              />
              <Popover
                active={filterPopoverActive}
                triggerElement={
                  <IconButton
                    title="Filter"
                    color="brand"
                    variant="light"
                    spacing="compact"
                  >
                    <FunnelIcon size="md" variant="regular" />
                  </IconButton>
                }
                onToggle={() => setFilterPopoverActive((prev) => !prev)}
                position="bottom-right"
              >
                <div style={{ width: "500px" }}>
                  <Filter
                    metadata={[
                      {
                        entity: {
                          name: "Contractor",
                        },
                        values: ["blocked", "unblocked"].map((value) => ({
                          name: value,
                          label: value.toUpperCase(),
                        })),
                      },
                    ]}
                    currentState={tableFilters}
                    onChange={setTableFilters}
                    onClose={() => setFilterPopoverActive((prev) => !prev)}
                  />
                </div>
              </Popover>
            </div>
            <div
              style={{ display: "felx", gap: "8px" }}
              className="mt-4 mt-lg-0"
            >
              <DownloadButton
                isPending={downloadInProgress}
                selectedRows={selectedRows}
                onClick={handleDownloadClick}
              />
            </div>
          </div>
        }
        footer={
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <span className="small">
              Showing:{" "}
              {renderPaginationText(
                totalNumberOfRecords,
                currentPageNumber,
                ITEMS_PER_PAGE
              )}
            </span>
            <Pagination
              currentPage={currentPageNumber}
              totalPageCount={Math.ceil(totalNumberOfRecords / ITEMS_PER_PAGE)}
              onChange={setCurrentPageNumber}
              terminalNeighbourCount={1}
              neighbourCount={1}
              jumpToLabel="Go to"
            />
          </div>
        }
        {...getTableProps()}
        data={contractors}
      />

      <Drawer
        open={isAddContractorModalOpen}
        onClose={() => setIsAddContractorModalOpen(false)}
        padding={0}
        stickPosition="right"
        withCloseButton
        size="800px"
        borderRadius="16px"
      >
        <AddContractorForm
          editValues={userInputToBeEdited}
          contractorId={userInputToBeEdited?._id}
          onContractorAdded={() => setIsAddContractorModalOpen(false)}
        />
      </Drawer>
      <Modal
        active={isBulkBlockContractorModalOpen}
        onClose={() => setIsBulkBlockContractorModalOpen(false)}
      >
        <div className="modalWrapper">
          <button
            className="btn abs-top-right-btn light-gray"
            onClick={() => setIsBulkBlockContractorModalOpen(false)}
          >
            x
          </button>
          <p className="primary-color fs-16 fw-700">Block Contractor</p>
          <p className="fs-14 primary-color mb-4">
            Do you want to block these contractors with Contractor ID:{" "}
            {selectedRows.map((eachRow) => (
              <b>{eachRow._id} </b>
            ))}
            ?
          </p>
          <div className="d-flex">
            <button
              className="btn primary-button w-unset px-5 fs-14 mr-3 b-color-secondary"
              onClick={() => {
                bulkBlockOrUnblockContractor(ContractorActions.BLOCK);
              }}
            >
              Yes
            </button>
            <button
              className="btn outline-btn fs-14 b-color-secondary secondary-color px-5"
              onClick={() => setIsBulkBlockContractorModalOpen(false)}
            >
              No
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
